/**
 * Very tall pages (a website saved as one long PDF page, a long screenshot)
 * are split into parts, each read as an ordinary page (plan
 * menu-import-fixes F5). Pure: where to cut. The drawing lives in
 * `src/lib/menu-capture/tall-parts.ts`.
 *
 * A part is at most `partMaxHeight(width)` rows. It ends on the quietest row
 * band of its last fifth (between two lines of text, usually between two
 * dishes), and the next part starts on the quietest band 8–16% of a part
 * above that cut, so a dish or heading cut in two is whole in the next part;
 * the read merges the twins of that overlap.
 */

/** Taller than this many times its width: split. A4 is 1.41, a phone screenshot about 2.2. */
export const TALL_PAGE_RATIO = 3;
/** Width of the parts (the read's text scale fits in it). */
export const PART_MAX_WIDTH = 1280;
/** The server refuses pages with a side under 400 px; parts keep a margin. */
export const PART_MIN_HEIGHT = 600;
/** Narrow pages are drawn at least this wide (the server's 400 px minimum, with a margin). */
export const PART_MIN_WIDTH = 480;
/** The server refuses pages with a side over 4096 px. */
export const PART_MAX_SIDE = 4096;
/** At most this many parts per page (the rest is left out, with a message). */
export const PARTS_PER_PAGE_MAX = 40;
/** The cut is searched in this last share of a part. */
const CUT_ZONE = 0.2;
/** The next part starts this share of a part above the cut. */
const OVERLAP_MIN = 0.08;
const OVERLAP_MAX = 0.16;
/** The row ink is averaged over this share of the width, above and below. */
const SMOOTHING = 0.01;

export function isTallPage(width: number, height: number): boolean {
  return width > 0 && height / width > TALL_PAGE_RATIO;
}

/** The tallest part for a page this wide: about a phone screen, within the server's limits. */
export function partMaxHeight(width: number): number {
  return Math.min(PART_MAX_SIDE, Math.max(1800, Math.round(2.2 * width)));
}

/**
 * Per row of an RGBA window, how much the brightness changes from pixel to
 * pixel (text rows change a lot, the gaps between lines barely), averaged
 * over a few rows so a single thin gap inside a line of text does not count.
 */
export function rowInk(
  rgba: Uint8ClampedArray,
  width: number,
  height: number
): Float64Array {
  const raw = new Float64Array(height);
  for (let y = 0; y < height; y++) {
    let change = 0;
    let previous = -1;
    for (let x = 0; x < width; x++) {
      const offset = (y * width + x) * 4;
      const grey =
        0.299 * rgba[offset] +
        0.587 * rgba[offset + 1] +
        0.114 * rgba[offset + 2];
      if (previous >= 0) change += Math.abs(grey - previous);
      previous = grey;
    }
    raw[y] = change;
  }
  const radius = Math.max(1, Math.round(width * SMOOTHING));
  const smoothed = new Float64Array(height);
  for (let y = 0; y < height; y++) {
    const from = Math.max(0, y - radius);
    const to = Math.min(height - 1, y + radius);
    let sum = 0;
    for (let k = from; k <= to; k++) sum += raw[k];
    smoothed[y] = sum / (to - from + 1);
  }
  return smoothed;
}

/** The quietest row between `from` and `to` (inclusive, clamped); ties go to the lower row. */
export function quietestRow(
  ink: Float64Array,
  from: number,
  to: number
): number {
  const start = Math.max(0, Math.min(from, ink.length - 1));
  const end = Math.max(start, Math.min(to, ink.length - 1));
  let best = start;
  for (let y = start; y <= end; y++) {
    if (ink[y] <= ink[best]) best = y;
  }
  return best;
}

export interface IPartCut {
  /** Where the part ends (exclusive), in page rows. */
  bottom: number;
  /** Where the next part starts, in page rows; null after the last part. */
  nextTop: number | null;
}

/**
 * The part that starts at row `top` of a page `pageHeight` rows tall, given
 * the ink of its window (rows `top` to `top + ink.length`, at most
 * `maxHeight` of them): where it ends and where the next part starts. A
 * window that reaches the end of the page is the last part. The next part
 * never starts before this one and is never shorter than PART_MIN_HEIGHT.
 */
export function cutPart(
  ink: Float64Array,
  top: number,
  pageHeight: number,
  maxHeight: number
): IPartCut {
  const windowHeight = ink.length;
  if (top + windowHeight >= pageHeight) {
    return { bottom: pageHeight, nextTop: null };
  }
  const cut = quietestRow(
    ink,
    Math.floor(windowHeight * (1 - CUT_ZONE)),
    windowHeight - 1
  );
  const next = quietestRow(
    ink,
    cut - Math.round(maxHeight * OVERLAP_MAX),
    cut - Math.round(maxHeight * OVERLAP_MIN)
  );
  const nextTop = Math.max(
    top + 1,
    Math.min(top + next, pageHeight - PART_MIN_HEIGHT)
  );
  return { bottom: top + cut, nextTop };
}
